import { Injectable } from "@angular/core"
import { AlertData } from "../models/alertData.model"
import { WindowSizeService } from "./window-size.service"
import { EMPTY, Observable } from "rxjs"
import { ModalService } from "../shared-components/modal/modal.service"
import { catchError, map } from "rxjs/operators"
import Utils from "../utils/utils"
import { LmApiService } from "./lm-api.service"
import { ADDON_LOCAL_STORAGE_KEY } from "../models/constants"
import { NotificationsService } from "angular2-notifications"

@Injectable()
export class AddOnViewService {
    // iframe-add-on experience is defined by '/add-on/' url segment, based on this segment, the experience is true | false
    private isAddonExperience = Utils.isAddOnExperienceLink(window.location.pathname, "/add-on/")
    private alertData: AlertData = {
        classes: "search-page",
        text_color: "#000",
        page_regex: "(/add-on/resource/)",
        day_number: 90,
        mobile_background_color: "#81d4ed1f",
        background_color: "#81d4ed1f",
        call_to_action: "VISIT THE FULL SITE",
        link: "/",
        link_target: "_blank",
        title: "Bring the world to your Google Classroom",
        text:
            "PBS LearningMedia has curated FREE standards-aligned videos and a wealth of high-quality educational media for you and your students." +
            " Visit our full experience to see our full suite of lesson plans, curated collections, and professional development to enrich your classroom.",
    }
    isTeacherPreview: boolean = true

    constructor(
        private windowSizeService: WindowSizeService,
        private modalService: ModalService,
        private lmApiService: LmApiService,
        private notificationService: NotificationsService
    ) {
        this.modalService.modalBackDrop.instance.clickOutsideToClose = !this.isAddonExperience
        window.addEventListener("message", this.listenForPopUpClose.bind(this), false)
    }

    // TODO NEEDS REFACTORING
    // the return state of this function will decide if log in is required or not and based on return state provided by
    // this function, the iframeGuard will redirect user to login page experience
    public isLoginRequired(isUserLoggedIn: boolean, isStudent: boolean): boolean {
        let params = Utils.extractUrlQueryParams(window.location.href)

        if (params && Utils.isSegmentContainedInUrl("resource")) {
            // this check is intended to allow users (student/teacher) to preview resource without any log in data
            this.isTeacherPreview = Object.keys(params).indexOf("attachmentId") === -1

            if (params && Object.keys(params).indexOf("auto-assign") !== -1) {
                return false
            }

            return this.isTeacherPreview
        }
        // this check does not allow users to login in iframe experience with student account; if logged in with student account, then
        // the users will be logged off and asked to log in with a teacher account.
        else if (isUserLoggedIn && isStudent) {
            return true
        }
        return !isUserLoggedIn
    }

    get isAddonView() {
        return this.isAddonExperience
    }

    get classroomAlertData() {
        return this.alertData
    }

    /**
     * Extract add on query parameters from url
     * and add them to local storage as a stringified object
     * addon-iframe-params = {
     *   addOnToken: "",
     *   postId: "",
     *   courseId: "",
     *   hd: "",
     *   login_hint: "",
     * }
     */
    public storeAddonIframeParams(): void {
        if (!this.isAddonView || !Utils.isLocalStorageAvailable()) return

        // get parsed iframe params object from local storage, else create
        let iframeParams = localStorage.getItem(ADDON_LOCAL_STORAGE_KEY)
        iframeParams = iframeParams
            ? JSON.parse(iframeParams)
            : {
                  addOnToken: "",
                  postId: "",
                  courseId: "",
                  hd: "",
                  login_hint: "",
              }

        // extract all queryParameters
        let queryParams = Utils.extractUrlQueryParams(window.location.href)
        if (!queryParams)
            return localStorage.setItem(ADDON_LOCAL_STORAGE_KEY, JSON.stringify(iframeParams))

        // create two-dimensional array
        // [[addOnToken, ''], [postId, ''], ...]
        let entries = Object.entries(queryParams)

        // filter out unneeded query parameters, e.g.: ?q=*, student=true
        let filtered = entries.filter(([key, value]) => {
            return Object.keys(iframeParams).includes(key)
        })

        // @ts-ignore
        queryParams = Object.fromEntries(filtered)

        // merge objects, second object will take precedence
        // @ts-ignore
        iframeParams = { ...iframeParams, ...queryParams }

        // save in localstorage
        localStorage.setItem(ADDON_LOCAL_STORAGE_KEY, JSON.stringify(iframeParams))
    }

    /**
     * Returns a concatenated string with all the add-on query parameters from local storage
     */
    public static getAddOnQueryParameters(): string {
        const { courseId = "", postId = "", addOnToken = "" } = JSON.parse(
            localStorage.getItem(ADDON_LOCAL_STORAGE_KEY)
        )

        return `courseId=${courseId}` + `&postId=${postId}` + `&addOnToken=${addOnToken}`
    }

    public static getAddOnLoginQueryParameters(): string {
        const { hd = "", login_hint = "" } = JSON.parse(
            localStorage.getItem(ADDON_LOCAL_STORAGE_KEY)
        )

        return `hd=${hd}` + `&login_hint=${login_hint}`
    }

    // TODO When the update for POST method on Django is made, update also this call from GET to a POST
    public createAttachment(resourceCode): Observable<void> {
        const {
            courseId = "",
            postId = "",
            addOnToken = "",
            hd = "",
            login_hint = "",
        } = JSON.parse(localStorage.getItem(ADDON_LOCAL_STORAGE_KEY))
        const url = "/add-on/create_attachment/"
        const options = {
            params: {
                resourceCode,
                courseId,
                postId,
                addOnToken,
                hd,
                login_hint,
            },
        }

        // we open up the modal first,
        // to bypass safari trusted event issue (pop-up)
        const popUpWindowRef = Utils.openPopUp("")
        popUpWindowRef.document.write(
            "<div style='position:absolute; top:50%; left:50%; transform:translateX(-50%)translateY(-50%)' >Loading, Please wait...</div>"
        )

        // stop event listener from global dom
        return this.lmApiService.get(url, options).pipe(
            map((response) => {
                // set default for location, if not received from api response
                const { location = null } = response

                // if status 200 and no location, it means the user granted permission,
                // we close the popup
                if (!location) {
                    AddOnViewService.closeAddonIframe()
                    return popUpWindowRef.close()
                }

                // if status 200 and location, it means the user did not grant permission,
                // we should redirect to new location
                popUpWindowRef.location = location
            }),
            catchError((err) => {
                // if no status or any other status than 200 notify user and close the pop-up
                const msg = (err && err.error && err.error.msg) || ""
                popUpWindowRef.close()
                this.lmApiService.handleErrorForNonCritical(msg)
                return EMPTY
            })
        )
    }

    private static closeAddonIframe(): void {
        window.parent.postMessage(
            {
                type: "Classroom",
                action: "closeIframe",
            },
            "*"
        )
    }

    private listenForPopUpClose(event) {
        if (!event || !event.data) return

        const { message, type } = event.data
        if (message !== "close" || type !== "Classroom") return

        this.notificationService.info(
            "Permissions Granted",
            "Please click assign again.",
            Utils.alertConfiguration
        )
        window.removeEventListener("message", this.listenForPopUpClose, false)
    }
}
